import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useBranding } from "../lib/branding";
import { formatApiErrorDetail } from "../lib/api";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
    const { login } = useAuth();
    const { tenant } = useBranding();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const tagline =
        tenant?.branding?.login_tagline ||
        "Access all your performance metrics in real-time. Manage, customize and optimize.";

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const u = await login(email, password);
            toast.success(`Welcome back, ${u.full_name || u.email}`);
            navigate("/dashboard");
        } catch (e) {
            const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Login failed";
            setErr(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-screen flex bg-zinc-50 text-zinc-900"
            data-testid="login-page"
        >
            {/* Left: branding */}
            <div className="hidden lg:flex flex-col w-1/2 relative overflow-hidden border-r border-zinc-900">
                <div
                    className="absolute inset-0 opacity-40"
                    style={{
                        background:
                            "radial-gradient(900px 600px at 0% 0%, var(--wl-primary-hex) 0%, transparent 60%), radial-gradient(800px 600px at 100% 100%, var(--wl-secondary-hex) 0%, transparent 60%)",
                    }}
                />
                <div className="absolute inset-0 grid-bg opacity-50" />
                <div className="relative z-10 p-12 flex flex-col items-center justify-center h-full">
                    <div className="text-center w-full max-w-md">
                        {/* Agency logo (if branding.logo_url set) — falls back to Lumina Reports logo */}
                        <img
                            src={tenant?.branding?.logo_url || "/lumina-logo.png"}
                            alt={tenant?.branding?.brand_name || tenant?.agency?.name || "Lumina Reports"}
                            className="h-24 sm:h-28 lg:h-32 w-auto mx-auto mb-6 object-contain"
                            data-testid="login-logo"
                            onError={(e) => {
                                // If the agency-provided URL fails, gracefully fall back to Lumina
                                if (e.currentTarget.src.indexOf("/lumina-logo.png") === -1) {
                                    e.currentTarget.src = "/lumina-logo.png";
                                }
                            }}
                        />
                        <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight tracking-tight">
                            Multiple Metrics.
                            <br />
                            <span className="wl-accent-text">Agile Narrative.</span>
                        </h1>
                        <p className="text-zinc-500 mt-5 text-base mx-auto leading-relaxed">
                            {tagline}
                        </p>
                    </div>
                </div>
            </div>

            {/* Right: form */}
            <div className="flex-1 flex items-center justify-center px-6 py-10">
                <div className="w-full max-w-md">
                    <div className="overline mb-2">Sign in</div>
                    <h2 className="font-display text-3xl font-bold mb-2">
                        Welcome back
                    </h2>
                    <p className="text-zinc-500 text-sm mb-8">
                        Access your reporting workspace.
                    </p>

                    <form onSubmit={onSubmit} className="space-y-4">
                        <div>
                            <label
                                htmlFor="email"
                                className="block text-xs text-zinc-500 mb-1.5 uppercase tracking-wider"
                            >
                                Email
                            </label>
                            <input
                                id="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@agency.com"
                                className="w-full px-3.5 py-2.5 bg-zinc-100 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all"
                                style={{
                                    "--tw-ring-color": "var(--wl-primary-hex)",
                                }}
                                data-testid="login-email-input"
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="password"
                                className="block text-xs text-zinc-500 mb-1.5 uppercase tracking-wider"
                            >
                                Password
                            </label>
                            <input
                                id="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="w-full px-3.5 py-2.5 bg-zinc-100 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2"
                                data-testid="login-password-input"
                            />
                        </div>

                        {err && (
                            <div
                                className="text-sm text-red-400 bg-red-500/5 border border-red-500/20 px-3 py-2 rounded-md"
                                data-testid="login-error"
                            >
                                {err}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium text-sm transition-all duration-200 disabled:opacity-50"
                            style={{
                                background: "var(--wl-primary-hex)",
                                color: "#ffffff",
                            }}
                            data-testid="login-submit-btn"
                        >
                            {loading ? "Signing in…" : "Sign in"}
                            {!loading && <ArrowRight className="h-4 w-4" />}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
